import * as React from "react";
import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface DatePickerInputProps {
  /** Aceita Date, string ISO/YYYY-MM-DD ou null */
  value?: Date | string | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  id?: string;
}

const DISPLAY_FORMAT = "dd/MM/yyyy";

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return isValid(dt) ? dt : null;
    }
    const dt = new Date(value);
    return isValid(dt) ? dt : null;
  }
  return null;
}

function applyMask(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts: string[] = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join("/");
}

export function DatePickerInput({
  value,
  onChange,
  placeholder = "dd/mm/aaaa",
  disabled,
  minDate,
  maxDate,
  className,
  id,
}: DatePickerInputProps) {
  const dateValue = toDate(value);
  const [text, setText] = React.useState<string>(
    dateValue ? format(dateValue, DISPLAY_FORMAT) : ""
  );
  const [open, setOpen] = React.useState(false);

  // Sincroniza quando o value externo muda
  React.useEffect(() => {
    const d = toDate(value);
    setText(d ? format(d, DISPLAY_FORMAT) : "");
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyMask(e.target.value);
    setText(masked);
    if (masked.length === 0) {
      onChange(null);
      return;
    }
    if (masked.length === 10) {
      const parsed = parse(masked, DISPLAY_FORMAT, new Date());
      if (isValid(parsed)) {
        if (minDate && parsed < minDate) return;
        if (maxDate && parsed > maxDate) return;
        onChange(parsed);
      }
    }
  };

  const handleBlur = () => {
    if (text.length > 0 && text.length < 10) {
      // texto incompleto: restaura o value válido
      setText(dateValue ? format(dateValue, DISPLAY_FORMAT) : "");
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        inputMode="numeric"
        className="pr-10"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            aria-label="Abrir calendário"
          >
            <CalendarIcon className="h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            locale={ptBR}
            selected={dateValue ?? undefined}
            onSelect={(d) => {
              if (d) {
                onChange(d);
                setText(format(d, DISPLAY_FORMAT));
              }
              setOpen(false);
            }}
            disabled={(d) => {
              if (minDate && d < minDate) return true;
              if (maxDate && d > maxDate) return true;
              return false;
            }}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
